import { AgenticFeatureProvider, AgenticFeatureRegistry } from './AgenticFeatureRegistry';

/**
 * Example implementation of an AgenticFeatureProvider
 * This demonstrates how external systems can integrate with the chat interface
 */
export class ExampleAnalyticsFeatureProvider implements AgenticFeatureProvider {
  id = 'analytics-provider';
  name = 'Analytics Feature Provider';

  // Track analytics data
  private sessionData = {
    promptsSent: 0,
    conversationsCleared: 0,
    agentChanges: 0,
    completedRuns: 0,
  };

  constructor() {
    // Auto-register when instantiated
    this.register();
  }

  register() {
    AgenticFeatureRegistry.register(this);
    console.log('Analytics provider registered');
  }

  unregister() {
    AgenticFeatureRegistry.unregister(this.id);
    console.log('Analytics provider unregistered');
  }

  // Lifecycle event handlers
  onAgentIdChange = (agentId: string) => {
    this.sessionData.agentChanges++;
    console.log(`Analytics: Agent ID changed to ${agentId}`, this.sessionData);
    
    // Could send analytics to external service
    this.sendAnalytics('agent_change', { agentId, count: this.sessionData.agentChanges });
  };

  onConversationClear = () => {
    this.sessionData.conversationsCleared++;
    console.log('Analytics: Conversation cleared', this.sessionData);
    
    this.sendAnalytics('conversation_clear', { count: this.sessionData.conversationsCleared });
  };

  onPromptSent = (prompt: string) => {
    this.sessionData.promptsSent++;
    console.log(`Analytics: Prompt sent (${prompt.length} chars)`, this.sessionData);
    
    this.sendAnalytics('prompt_sent', { 
      promptLength: prompt.length, 
      count: this.sessionData.promptsSent 
    });
  };

  onRunCompleted = (messages: any[]) => {
    this.sessionData.completedRuns++;
    console.log(`Analytics: Run completed with ${messages.length} messages`, this.sessionData);
    
    this.sendAnalytics('run_completed', { 
      messageCount: messages.length, 
      count: this.sessionData.completedRuns 
    });
  };

  // Helper methods
  getPreferredAgentId = (): string | null => {
    // Could return a user-specific or context-specific agent ID
    // For example, based on user role or current page context
    const userRole = this.getCurrentUserRole();
    
    switch (userRole) {
      case 'admin':
        return 'admin-specialist-agent';
      case 'sales':
        return 'sales-assistant-agent';
      case 'support':
        return 'customer-support-agent';
      default:
        return null;
    }
  };

  getInitialPrompts = (): string[] => {
    // Could return context-specific initial prompts
    const userRole = this.getCurrentUserRole();
    
    switch (userRole) {
      case 'admin':
        return [
          'Show me system health metrics',
          'What are today\'s key performance indicators?'
        ];
      case 'sales':
        return [
          'Show me today\'s sales performance',
          'What are the trending products?'
        ];
      case 'support':
        return [
          'Show me recent customer issues',
          'What are the most common support tickets?'
        ];
      default:
        return [];
    }
  };

  // Mock methods for demonstration
  private getCurrentUserRole(): string {
    // In a real implementation, this would get the actual user role
    return 'admin'; // Mock return
  }

  private sendAnalytics(event: string, data: any) {
    // In a real implementation, this would send data to an analytics service
    console.log(`📊 Analytics Event: ${event}`, data);
  }

  // Public API methods
  getSessionData() {
    return { ...this.sessionData };
  }

  resetSessionData() {
    this.sessionData = {
      promptsSent: 0,
      conversationsCleared: 0,
      agentChanges: 0,
      completedRuns: 0,
    };
  }
}

/**
 * Example of a context-aware feature provider
 */
export class ExampleContextFeatureProvider implements AgenticFeatureProvider {
  id = 'context-provider';
  name = 'Context Feature Provider';

  onPromptSent = (prompt: string) => {
    // Analyze prompt for context and maybe inject additional context
    if (prompt.toLowerCase().includes('order') || prompt.toLowerCase().includes('purchase')) {
      console.log('🛒 Context: E-commerce related query detected');
      // Could inject context about current user's order history
    }
    
    if (prompt.toLowerCase().includes('customer') || prompt.toLowerCase().includes('user')) {
      console.log('👤 Context: Customer service query detected');
      // Could inject context about customer support tools
    }
  };

  getPreferredAgentId = (): string | null => {
    // Could analyze current page context
    const currentPage = window.location.pathname;
    
    if (currentPage.includes('/orders')) {
      return 'order-management-agent';
    } else if (currentPage.includes('/customers')) {
      return 'customer-service-agent';
    } else if (currentPage.includes('/products')) {
      return 'product-catalog-agent';
    }
    
    return null;
  };
}

export class ExampleAnalyticsDashboardProvider implements AgenticFeatureProvider {
  id = 'example-analytics-dashboard';
  name = 'Analytics Dashboard Integration';

  onAgentIdChange(agentId: string): void {
    if (agentId === 'analytics-dashboard') {
      console.log('Analytics Dashboard: Agent activated for dashboard creation');
      // Could initialize dashboard creation UI components
    }
  }

  onConversationClear(): void {
    console.log('Analytics Dashboard: Conversation cleared - resetting dashboard state');
    // Could clear any pending dashboard configurations
  }

  onFocusChange(focusKey: string): void {
    console.log('Analytics Dashboard: Focus changed to:', focusKey);
    // Could update UI to show which dashboard is focused
  }

  getPromptForFocus(focusKey: string): string | null {
    // TEMPORARILY DISABLED - Commenting out automatic focus context to simplify dashboard creation testing
    // When focus changes to a dashboard ID, return a prompt to load that dashboard
    // if (focusKey) {
    //   console.log(`Analytics Dashboard: Generating focus prompt for dashboard: ${focusKey}`);
    //   return `We are now working with dashboard ID '${focusKey}'. Please load the this dashboard by id and set it as your current context. prompts should now be related to this dashboad and requests for information or changes should be addressed to this dashboard.`;
    // }
    console.log(`Analytics Dashboard: Focus prompt disabled for testing (focusKey: ${focusKey})`);
    return null;
  }

  onPromptSent(prompt: string): void {
    console.log('Analytics Dashboard: Prompt sent:', prompt);
    
    // Track dashboard-related prompts
    if (prompt.toLowerCase().includes('dashboard') || 
        prompt.toLowerCase().includes('analytics') || 
        prompt.toLowerCase().includes('chart')) {
      this.trackDashboardInteraction('prompt', { prompt });
    }
  }

  onRunCompleted(messages: any[]): void {
    console.log('Analytics Dashboard: Run completed, checking for dashboard configurations');
    
    // Look for dashboard configurations in the response
    messages.forEach(message => {
      if (message.content && message.content[0]?.text) {
        const text = message.content[0].text;
        
        // Check if response contains a dashboard configuration
        if (text.includes('"dashboardType"') && text.includes('"tabs"')) {
          this.handleDashboardResponse(text);
        }
        
        // Check for validation results
        if (text.includes('validation') && text.includes('isValid')) {
          this.handleValidationResponse(text);
        }
      }
    });
  }

  getPreferredAgentId(): string | null {
    // Suggest analytics dashboard agent when on analytics pages
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname.includes('/analytics') || 
          pathname.includes('/dashboard') || 
          pathname.includes('/reports')) {
        return 'analytics-dashboard';
      }
    }
    return null;
  }

  getInitialPrompts(): string[] {
    return [
      'Create a sales performance dashboard with revenue trends and top performers',
      'Build an executive summary dashboard with key business metrics',
      'Generate an operations dashboard with efficiency and labor metrics',
      'Create a customer analytics dashboard with retention and satisfaction data',
      'Build a geographic performance dashboard with location-based insights',
      'Generate a financial dashboard with revenue, costs, and profitability analysis'
    ];
  }

  private handleDashboardResponse(responseText: string): void {
    try {
      // Extract JSON configuration from the response
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch && jsonMatch[0]) {
        const dashboardConfig = JSON.parse(jsonMatch[0]);
        
        console.log('Dashboard configuration received:', dashboardConfig);
        
        // Dispatch custom event for UI components to handle
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('dashboardConfigReceived', {
            detail: { 
              config: dashboardConfig,
              timestamp: Date.now(),
              source: 'analytics-dashboard-ai'
            }
          }));
        }
        
        // Track successful dashboard creation
        this.trackDashboardInteraction('created', { 
          dashboardName: dashboardConfig.name,
          tabCount: dashboardConfig.tabs?.length || 0,
          hasValidation: !!dashboardConfig.id
        });
      }
    } catch (error) {
      console.error('Failed to parse dashboard configuration:', error);
      this.trackDashboardInteraction('error', { error: error.message });
    }
  }

  private handleValidationResponse(responseText: string): void {
    try {
      // Look for validation result patterns
      const isValid = responseText.includes('✅') || responseText.includes('valid and ready');
      const hasErrors = responseText.includes('❌') || responseText.includes('validation errors');
      
      console.log('Dashboard validation result:', { isValid, hasErrors });
      
      // Dispatch validation event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('dashboardValidationResult', {
          detail: {
            isValid,
            hasErrors,
            message: responseText,
            timestamp: Date.now()
          }
        }));
      }
      
      this.trackDashboardInteraction('validated', { isValid, hasErrors });
    } catch (error) {
      console.error('Failed to process validation response:', error);
    }
  }

  private trackDashboardInteraction(type: string, data: any): void {
    // Analytics tracking for dashboard interactions
    console.log(`Analytics Dashboard Interaction: ${type}`, data);
    
    // Could send to analytics service
    // analyticsService.track('dashboard_ai_interaction', { type, ...data });
  }
}

// Example usage - these would typically be instantiated by the main application
export const initializeExampleProviders = () => {
  // These could be instantiated based on user permissions, current context, etc.
  const analyticsProvider = new ExampleAnalyticsFeatureProvider();
  const contextProvider = new ExampleContextFeatureProvider();
  
  return {
    analyticsProvider,
    contextProvider,
  };
};

// Example of how external code can interact with the registry
export const exampleExternalInteractions = () => {
  // External code can change the agent ID
  AgenticFeatureRegistry.changeAgentId('external-suggested-agent');
  
  // External code can clear the conversation
  AgenticFeatureRegistry.clearConversation();
  
  // External code can get current settings
  const settings = AgenticFeatureRegistry.getSettings();
  console.log('Current settings:', settings);
  
  // External code can check what providers are registered
  const providers = AgenticFeatureRegistry.getAllProviders();
  console.log('Registered providers:', providers.map(p => p.name));
}; 

// Example of how to register the analytics dashboard provider
export const initializeAnalyticsDashboardProvider = () => {
  const analyticsProvider = new ExampleAnalyticsDashboardProvider();
  AgenticFeatureRegistry.register(analyticsProvider);
  
  console.log('Analytics Dashboard Provider registered');
  
  // Set up event listeners for dashboard handling
  if (typeof window !== 'undefined') {
    window.addEventListener('dashboardConfigReceived', handleDashboardConfig);
    window.addEventListener('dashboardValidationResult', handleValidationResult);
  }
};

// Event handlers for dashboard workflow
const handleDashboardConfig = (event: CustomEvent) => {
  const { config, timestamp } = event.detail;
  
  console.log('Handling received dashboard configuration:', config);
  
  // Example workflow:
  // 1. Show preview modal
  showDashboardPreview(config);
  
  // 2. Offer validation if not already validated
  if (!config.id) {
    offerDashboardValidation(config);
  }
  
  // 3. Provide save/edit options
  showDashboardActions(config);
};

const handleValidationResult = (event: CustomEvent) => {
  const { isValid, hasErrors, message } = event.detail;
  
  console.log('Handling validation result:', { isValid, hasErrors });
  
  if (isValid) {
    showSuccessMessage('✅ Dashboard configuration is valid and ready to use!');
  } else {
    showValidationErrors(message);
  }
};

// UI Helper functions (would be implemented based on your UI framework)
const showDashboardPreview = (config: any) => {
  console.log('Showing dashboard preview for:', config.name);
  // Implementation would show a modal or panel with dashboard preview
};

const offerDashboardValidation = (config: any) => {
  console.log('Offering validation for dashboard:', config.name);
  // Implementation would show validation button/option
};

const showDashboardActions = (config: any) => {
  console.log('Showing dashboard actions for:', config.name);
  // Implementation would show save, edit, download options
};

const showSuccessMessage = (message: string) => {
  console.log('Success:', message);
  // Implementation would show success notification
};

const showValidationErrors = (message: string) => {
  console.log('Validation errors:', message);
  // Implementation would show error notification with details
};

// Example external interactions for analytics dashboards
export const analyticsExternalInteractions = () => {
  // Change to analytics dashboard agent when on analytics page
  if (typeof window !== 'undefined' && window.location.pathname.includes('/analytics')) {
    AgenticFeatureRegistry.setFeatureContext('analytics-dashboard');
  }
  
  // Get initial prompts from registered providers
  const initialPrompts = AgenticFeatureRegistry.getInitialPrompts();
  
  console.log('Analytics initial prompts:', initialPrompts);
  
  // Example of using specific analytics features
  const examples = {
    // Get analytics-specific assistant
    getAnalyticsAssistant: async () => {
      const settings = AgenticFeatureRegistry.getSettings();
      return await fetch('/api/ai/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          featureKey: 'analytics-dashboard',
          applicationKey: settings.applicationKey
        })
      }).then(r => r.json());
    },
    
    // Request specific dashboard type
    requestExecutiveDashboard: () => {
      AgenticFeatureRegistry.sendPrompt(
        'Create an executive summary dashboard with key business metrics, performance indicators, and strategic insights'
      );
    },
    
    // Request validation of existing config
    validateExistingDashboard: (config: any) => {
      AgenticFeatureRegistry.sendPrompt(
        `Please validate this dashboard configuration: ${JSON.stringify(config, null, 2)}`
      );
    }
  };
  
  return examples;
}; 